import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Divider, Grid, Header, Icon } from 'semantic-ui-react'
import { StyledGridColumns, StyledGridRow, StyledSegment, StyledFileLabel, StyledInfoRow } from './style'
import { b64toBlob, formatNumber, formatNumberWithoutFractions, handleErrors } from '~/utils/helpers'
import { useAuth } from '~/routes/components/AuthProvider'
import { useParams } from 'react-router-dom'
import MilkFixedService from '~/api/services/milkFixedPrice'
import Dialog from '~/components/Dialog'
import MyTimer from '~/components/Countdown'
import { SCHEDULED_STATUS } from '~/Constants'

interface ShowInfoProps {
  showData: any,
  activeIndex: number,
  tradingVolumeType?: string
  onCompleteTimer?: () => void
}

const ShowInfo: React.FC<ShowInfoProps> = (props: ShowInfoProps) => {
  const { t } = useTranslation()
  const [showData, setShowData] = useState(null)
  const { activeIndex } = props
  const { systemData: { amountUnit } } = useAuth()
  const { id } = useParams()
  const [fileSource, setFileSource] = useState(null)

  useEffect(() => {
    if (props.showData) {
      setShowData(props.showData)
    }
  }, [props.showData])

  const handleDownloadPdf = async (fileName, ife = false) => {
    setFileSource(true);
    try {
      let response
      if (ife) {
        response = await MilkFixedService.getFile(id, showData?.id, { fileName: fileName });
      } else {
        response = await MilkFixedService.getFileTradingSlot(id, { fileName: fileName })
      }
      const type = response.data.split(';')[0].split('data:')[1]
      const blob = b64toBlob(response.data, type);
      const blobUrl = URL.createObjectURL(blob);
      setFileSource(blobUrl);
    } catch (error) {
      setFileSource(false);
      handleErrors(error)
    }
  };

  function renderAttachmentLabel() {
    return <StyledFileLabel
      basic
      color='blue'
      onClick={() => handleDownloadPdf(showData?.ifePricesFile, true)}
    >
      <Icon name='file' />
      <div className='fileName'>{showData?.ifePricesFile}</div>
    </StyledFileLabel>
  }

  function renderFileLabel() {
    return <StyledFileLabel
      basic
      color='blue'
      onClick={() => handleDownloadPdf(showData?.filename)}
    >
      <Icon name='file' />
      <div className='fileName'>{showData?.originalFilename}</div>
    </StyledFileLabel>
  }

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
      isLoading={typeof (fileSource) === 'boolean' && fileSource}
    />
  }

  function renderRightColumn() {
    return <>
      {showData?.displayIfe && <StyledGridRow>
        <Grid.Column>{t('ife Institute - Last update at')}</Grid.Column>
        <StyledGridColumns>{showData?.ifePricesLastUpdate ? dayjs(showData?.ifePricesLastUpdate).format(('dddd, D MMMM YYYY HH:mm')) : t('Not defined')}</StyledGridColumns>
      </StyledGridRow>
      }
      <StyledGridRow>
        <Grid.Column>{t('Documents')}</Grid.Column>
        <StyledGridColumns>{showData?.filename ? renderFileLabel() : t('Not defined')}</StyledGridColumns>
      </StyledGridRow>
      <StyledInfoRow>
        <Grid.Column style={{ width: '50%', minWidth: '50%' }}>{t('Info')}</Grid.Column>
        <StyledGridColumns>{showData?.description ? showData.description : t('Not defined')}</StyledGridColumns>
      </StyledInfoRow>
    </>
  }

  function renderCountDown() {
    const timer = new Date(showData?.dateFrom)
    timer.setSeconds(timer.getSeconds() + 10) // set 10 seconds later so db can store in time status
    return <MyTimer
      style={{ marginBottom: '30px', marginTop: '-50px' }}
      expiryTimestamp={timer}
      onComplete={() => props.onCompleteTimer()}
    />
  }

  function getStyles() {
    const definedStatus = showData?.status === SCHEDULED_STATUS
    return definedStatus ? { display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}
  }

  function renderDetailsTabInfo() {
    return <StyledSegment placeholder>
      <Grid columns={2} stackable>
        <Grid.Row style={{ marginLeft: 10 }}>
          <Header>{showData?.uniqueId}</Header>
        </Grid.Row>
        <Divider vertical style={{ margin: 0 }} />
        <Grid.Column style={{ padding: '2rem' }}>
          <Grid columns={2} stackable>
            <StyledGridRow>
              <Grid.Column>{t('Status')}</Grid.Column>
              <StyledGridColumns>{showData?.statusLabel}</StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('Created')}</Grid.Column>
              <StyledGridColumns>{dayjs(showData?.createdAt).format(('dddd, D MMMM YYYY HH:mm'))}</StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('Start')}</Grid.Column>
              <StyledGridColumns>{dayjs(showData?.dateFrom).format(('dddd, D MMMM YYYY HH:mm'))}</StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('End')}</Grid.Column>
              <StyledGridColumns>{dayjs(showData?.dateTo).format(('dddd, D MMMM YYYY HH:mm'))}</StyledGridColumns>
            </StyledGridRow>
            {showData?.status === SCHEDULED_STATUS && renderRightColumn()}
          </Grid>
        </Grid.Column>
        <Grid.Column style={getStyles()}>
          <Grid columns={2} stackable style={{ marginLeft: 5 }}>
            {showData?.status === SCHEDULED_STATUS ? renderCountDown() : renderRightColumn()}
          </Grid>
        </Grid.Column>
      </Grid>
    </StyledSegment>
  }

  function renderFarmerGroupInfo() {
    return <StyledSegment placeholder>
      <Grid columns={2} stackable>
        <Divider vertical style={{ margin: 0 }} />
        <Grid.Row verticalAlign='middle'>
          <Grid.Column>
            <Grid columns={3} stackable>
              <StyledGridRow>
                <Grid.Column>{t('Name')}</Grid.Column>
                <StyledGridColumns>{showData.name}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Trading Amount (%/year)')}</Grid.Column>
                <StyledGridColumns>{`${showData.amountPercent ? formatNumber(showData.amountPercent) : 0} %`}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Number of farmers (in %)')}</Grid.Column>
                <StyledGridColumns>
                  {/*@ts-ignore*/}
                  {`${showData?.offeredFarmersCount} / ${showData?.farmersCount} (${showData?.offeredFarmersCount === 0 || showData?.farmersCount === 0 ? '0' : formatNumber(showData?.offeredFarmersCount / showData?.farmersCount * 100)}%)`}
                </StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Number of offers')}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(showData.offersCount)}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Offered amount (in {{amountUnit}})', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(showData.offeredAmount || '0')} {amountUnit}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Confirmed trading amount (in {{amountUnit}})', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(showData.confirmedAmount || '0')} {amountUnit}</StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
          <Grid.Column>
            <Grid columns={3} stackable>
              <StyledGridRow>
                <Grid.Column>{t('Average trading amount ({{amountUnit}} / month)', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(showData.monthlyTradingAmount)} {amountUnit}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Minimum contingent (in {{amountUnit}})', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{`${formatNumberWithoutFractions(showData.minimumContingent || 0)} ${amountUnit}`}</StyledGridColumns>
              </StyledGridRow>
              {showData?.displayIfe && <StyledGridRow>
                <Grid.Column>{t('ife prices')}</Grid.Column>
                <StyledGridColumns>{showData.ifePricesFile ? renderAttachmentLabel() : t('Not defined')}</StyledGridColumns>
              </StyledGridRow>
              }
              <StyledGridRow>
                <Grid.Column>{t('Trading volume Type')}</Grid.Column>
                <StyledGridColumns>{props.tradingVolumeType}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Trading amount ({{amountUnit}} / year)', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{`${formatNumberWithoutFractions(showData.tradingAmount || 0)} ${amountUnit}`}</StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </StyledSegment>
  }

  return <>
    {showData ? activeIndex > 0 ? renderFarmerGroupInfo() : renderDetailsTabInfo() : null}
    {showData && renderModalPreviewFile()}
  </>

}
export default ShowInfo
